import React from 'react';
import { Table, Button, Tag, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { formatISODateTime } from '../../../../utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';

export interface TransitRouteType {
    id: string;
    originHub: string;
    destinationHub: string;
    isValid: boolean;
    updatedAt: string;
}

interface TransitManagementTableProps {
    data: TransitRouteType[];
    loading: boolean;
    onDisable: (id: string) => void;
    onEnable: (id: string) => void;
    pagination?: any;
    onTableChange?: (pagination: any, filters: any, sorter: any) => void;
}

const TransitManagementTable: React.FC<TransitManagementTableProps> = ({
    data,
    loading,
    onDisable,
    onEnable,
    pagination,
    onTableChange
}) => {
    const { t } = useTranslation();
    const handleDisable = (id: string) => {
        onDisable(id);
    };

    const handleEnable = (id: string) => {
        onEnable(id);
    };

    const columns: TableColumnsType<TransitRouteType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => (
                <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
            )
        },
        {
            title: 'Origin Hub',
            dataIndex: 'originHub',
            key: 'originHub',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => (
                <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
            )
        },
        {
            title: 'Destination Hub',
            dataIndex: 'destinationHub',
            key: 'destinationHub',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => (
                <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
            )
        },
        {
            title: 'Validity',
            dataIndex: 'isValid',
            key: 'isValid',
            align: 'left' as const,
            ellipsis: false,
            render: (isValid: boolean) => {
                const text = isValid ? 'Valid' : 'Invalid';
                return (
                    <Tag color={isValid ? 'green' : 'red'}>
                        <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                    </Tag>
                );
            }
        },
        {
            title: 'Updated Time',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left' as const,
            ellipsis: false,
            render: (updatedAt: string) => {
                const text = updatedAt ? formatISODateTime(updatedAt) : '--';
                return (
                    <span className="text-sm text-gray-600" style={{ whiteSpace: 'nowrap' }}>
                        {text}
                    </span>
                );
            }
        },
        {
            title: t('central.sites.details.actions'),
            key: 'action',
            align: 'left' as const,
            fixed: 'right',
            ellipsis: false,
            render: (_, record: TransitRouteType) => (
                <div className="flex gap-2">
                    {record.isValid ? (
                        <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => handleDisable(record.id)}
                            style={{ paddingLeft: 0 }}
                        >
                            <span style={{ whiteSpace: 'nowrap' }}>Disable</span>
                        </Button>
                    ) : (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => handleEnable(record.id)}
                            style={{ paddingLeft: 0 }}
                        >
                            <span style={{ whiteSpace: 'nowrap' }}>Enable</span>
                        </Button>
                    )}
                </div>
            )
        }
    ];

    return (
        <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={false}
            size="middle"
            tableLayout="auto"
            className="transit-management-table mb-4"
        />
    );
};

export default TransitManagementTable;
